import { browser } from '$app/environment';
import { log } from '@yp/common/core.ts';
import { isPresent, omit, pick } from '@yp/common/identity.ts';
import { loadable, type Loadable } from '@yp/util.svelte/loadable.ts';
import type { ComponentProps } from 'svelte';
import { modals } from 'svelte-modals';
import { type Readable } from 'svelte/store';
import { InfoModal } from '../../gui/info_modal.ts';
import type { PromptSegment } from '../../gui/modals.ts';
import { IsChrome, IsIos, IsStandalone, summarize } from './private_detect.ts';

function canAddToHomeScreen() {
  // https://www.netguru.com/blog/pwa-ios
  return IsIos() && !IsStandalone();
}

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  readonly userChoice: 'accepted' | 'dismissed';
}

// N.B. For this class to work, you must add the following snippet to `app.html`:
// <script>
//   window.addEventListener('beforeinstallprompt', (e) => {
//     e.preventDefault();
//     window.beforeinstallpromptevent_ = e;
//   });
// </script>

export class PwaInstaller {
  static instance() {
    this.instance_ ??= new PwaInstaller();
    return this.instance_;
  }

  private static instance_: PwaInstaller | undefined;
  private deferredPrompt_: BeforeInstallPromptEvent | undefined;
  private canInstallNow_: boolean | undefined;
  private canInstall_: Loadable<boolean>;
  private setCanInstall_?: (value: boolean) => void;

  get canInstall(): Readable<boolean> {
    return this.canInstall_;
  }

  get canInstallNow(): boolean {
    return this.canInstallNow_ ?? false;
  }

  private constructor() {
    this.canInstall_ = loadable<boolean>(
      (set) => {
        this.setCanInstall_ = set;
        if (isPresent(this.canInstallNow_)) {
          this.setCanInstall_(this.canInstallNow_);
        }
      },
      undefined,
      'canInstall',
    );
    this.listenForInstallable_();
  }

  // DNS: Implement sharing of credentials using pwa cache:
  // https://stackoverflow.com/questions/62669966/how-to-maintain-login-status-in-a-pwa-initially-loaded-via-safari-14-ios-14/64136388#64136388
  async install() {
    if (isPresent(this.deferredPrompt_)) {
      await this.deferredPrompt_.prompt();
      console.log(this, pick(this.deferredPrompt_, 'userChoice'));
      const accepted = this.deferredPrompt_.userChoice === 'accepted';
      this.deferredPrompt_ = undefined;
      this.setInstallable(false);
      return accepted;
    }

    if (canAddToHomeScreen()) {
      // const props: MakeOptional<ComponentProps<InfoModal>, KeysOf<ComponentProps<InfoModal>, undefined>> = {
      const step1: PromptSegment[] = ['1. Tap', { type: 'icon', value: 'ios_share' }];
      let styleClass: string | undefined;
      let position: 'bottom' | 'top';
      if (IsChrome()) {
        step1.push('above, then…');
        styleClass = 'upArrowRight';
        position = 'top';
      } else {
        step1.push('below, then…');
        styleClass = 'downArrowCenter';
        position = 'bottom';
      }

      const props: ComponentProps<InfoModal> = {
        header: 'To install on your iPhone:',
        text: {
          lines: [
            step1,
            [
              '2. ',
              { type: 'icon', value: 'swipe_up' },
              'Swipe until you get to the bottom, then…',
            ],
            ['3. Select', { type: 'icon', value: 'add_box' }, 'Add to Home Screen'],
          ],
        },
        styleClass,
        position,
      };
      log('install', { styleClass, ...summarize() });
      modals.open(InfoModal, omit(props, 'isOpen'));
      return false;
    }

    console.error('App not installable.');
    return false;
  }

  private setInstallable(value: boolean) {
    this.canInstallNow_ = value;
    this.setCanInstall_?.(value);
  }

  private listenForInstallable_() {
    // TODO: There doesn't seem to be any place we can call this that will trigger early enough.
    // Instead, we rely on a snippet of code in <head> to capture the event. See above.
    // https://web.dev/learn/pwa/installation-prompt#the_beforeinstallprompt_event
    // log(this, 'Listening for install prompt.');
    // window.addEventListener('beforeinstallprompt', (e) => {
    //   e.preventDefault();
    //   this.deferredPrompt_ = e as BeforeInstallPromptEvent;
    //   log(this, 'App is installable.');
    //   this.setInstall_(true);
    // });
    if (browser) {
      if ('beforeinstallpromptevent_' in window) {
        this.deferredPrompt_ = window.beforeinstallpromptevent_ as
          | BeforeInstallPromptEvent
          | undefined;
      }
      if (isPresent(this.deferredPrompt_)) {
        log(this, 'App is installable.');
        this.setInstallable(true);
      } else if (canAddToHomeScreen()) {
        log(this, 'App can be added to home screen.');
        this.setInstallable(true);
      }
    }
  }
}
